import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { APIService } from 'src/app/shared/services/api.service';
import { CacheService } from 'src/app/shared/services/cache.service';
import { JwtService } from 'src/app/shared/services/jwt.service';

import { Subject } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  token = this.jwtService.getToken();

  public userUpdated$ = new Subject<any>();

  constructor(private http: HttpClient,
    private jwtService: JwtService,
    private apiService: APIService,
    private cacheService: CacheService) {
    this.userUpdated$.subscribe({
      next: () => {
        this.token = this.jwtService.getToken();
        this.cacheService.resetAll();
      },
    });
  }

  public isLoggedIn() {
    return !!this.token;
  }

  private setHeaders(): HttpHeaders {
    const headersConfig = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    if (this.jwtService.getToken()) {
      headersConfig['Authorization'] = `JWT ${this.jwtService.getToken()}`;
    }
    return new HttpHeaders(headersConfig);
  }

  public login(data) {
    const url = this.apiService.url('rest-auth/login/');

    const responseData = this.http.post<any>(url, data).pipe(
      tap((response: any) => {
        this.jwtService.saveToken(response.access);
        this.userUpdated$.next(true);
      }),
    );

    return responseData;
  }

  public isImpersonationActive() {
    return (
      localStorage.getItem('main-token') &&
      localStorage.getItem('impersonating') === 'true'
    );
  }

  public async impersonatedLogin(user) {
    const url = this.apiService.url('login-impersonated');
    const mainUserToken = this.jwtService.getToken();

    const response = await this.http.post<any>(url, { user })
      .toPromise();

    localStorage.setItem('main-token', mainUserToken);
    localStorage.setItem('impersonating', 'true');

    this.jwtService.saveToken(response.access);
    this.userUpdated$.next(true);

    return response;
  }

  public restoreUserFromImpersonation() {
    if (!this.isImpersonationActive()) {
      return;
    }

    const token = localStorage.getItem('main-token');
    localStorage.removeItem('main-token');
    localStorage.removeItem('impersonating');

    this.jwtService.saveToken(token);
    this.userUpdated$.next(true);

    window.location.href = '/';
  }

  public logout() {
    const url = this.apiService.url('rest-auth/logout/');

    const headers = this.setHeaders();
    this.jwtService.destroyToken();

    return this.http.post(url, null, {
      headers,
    });
  }

  public signUp(data) {
    const url = this.apiService.url('rest-auth/registration/');
    return this.http.post(url, data);
  }

  public forgotPassword(body) {
    const url = this.apiService.url('rest-auth/password/reset/');
    return this.http.post(url, body).toPromise();
  }

  public newPassword(body) {
    const url = this.apiService.url('rest-auth/password/reset/confirm/');
    return this.http.post(url, body).toPromise();
  }

  public verifyEmail(key: string, body) {
    const url = this.apiService.url(`rest-auth/registration/account-confirm-email/${key}/`);
    return this.http.post(url, body).toPromise();
  }

  public resendVerificationEmail(email: string) {
    const url = this.apiService.url('rest-auth/registration/resend-email/');
    return this.http.post(url, { email }).toPromise();
  }
}
